import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import {
  LayoutDashboard,
  Store,
  UserCheck,
  Bike,
  PackageSearch,
  PackageCheck,
  Warehouse,
  Truck,
  PackageOpen,
  Package as PackageIcon,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ShieldCheck,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" }],
  },
  {
    label: "Users",
    items: [
      { to: "/admin/store-owners", icon: Store, label: "Registered Store Owners" },
      { to: "/admin/store-owners/pending", icon: UserCheck, label: "Store Owners Pending" },
      { to: "/admin/riders", icon: Bike, label: "Registered Riders" },
      { to: "/admin/riders/pending", icon: UserCheck, label: "Riders Pending" },
    ],
  },
  {
    label: "Packages",
    items: [
      { to: "/admin/packages/waiting", icon: PackageSearch, label: "Waiting for Pickup" },
      { to: "/admin/packages/picked-up", icon: PackageOpen, label: "Picked Up by Rider" },
      { to: "/admin/packages/dropped", icon: Warehouse, label: "Dropped at Warehouse" },
      { to: "/admin/packages/warehouse", icon: Warehouse, label: "Received at Warehouse" },
      { to: "/admin/packages/sorting", icon: Warehouse, label: "Sorting / In Warehouse" },
      { to: "/admin/packages/shipped", icon: Truck, label: "Shipped Out" },
      { to: "/admin/packages/ready-last-mile", icon: PackageIcon, label: "Ready for Last-Mile" },
      { to: "/admin/packages/last-mile", icon: PackageIcon, label: "Out for Last-Mile" },
      { to: "/admin/packages/delivered", icon: PackageCheck, label: "Delivered to Customer" },
    ],
  },
  {
    label: "Warehouse",
    items: [
      { to: "/admin/outbound-batches", icon: Truck, label: "Outbound Batches" },
      { to: "/admin/warehouse-dispatchers", icon: UserCheck, label: "Warehouse Dispatchers" },
      { to: "/admin/warehouse-issues", icon: ShieldCheck, label: "Warehouse Issues" },
      { to: "/admin/warehouse-history", icon: LayoutDashboard, label: "Warehouse Activity" },
    ],
  },
  {
    label: "System",
    items: [{ to: "/admin/settings", icon: SettingsIcon, label: "Settings" }],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavBody = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
        <Logo className="h-11" />
        <button
          data-testid="sidebar-close-btn"
          onClick={() => setMobileOpen(false)}
          className="md:hidden text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-6">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">
              {group.label}
            </div>
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-100 hover:dark:bg-slate-900 border border-transparent",
                      ].join(" ")
                    }
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-slate-100/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
          <div className="w-8 h-8 rounded-full bg-cyan-400 text-slate-950 grid place-items-center font-display font-bold">
            {user?.name?.[0] || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name || "Admin"}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="mt-2 w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-100 hover:dark:bg-slate-900 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex-col fixed inset-y-0 left-0 z-30">
        <NavBody />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800">
            <NavBody />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 glass border-b border-slate-200 dark:border-slate-800 px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="sidebar-open-btn"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-slate-700 dark:text-slate-300 hover:text-slate-900 hover:dark:text-white"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">
              / Admin Console
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono-tech text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="hidden sm:inline">SYSTEMS ONLINE</span>
            <ThemeToggle size="sm" />
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
